import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
    MARKET_SPOT, MARKET_FUTURES, setCurrentMarket, getDefaultMarket, setDefaultMarket, getMarketHomePath
} from '../../utils/MarketStorage';
import { getCurrentEnvironment } from '../../utils/CurrentEnvironmentStorage';
import { getAutomationsCounts } from '../../services/AutomationsService';
import { getFuturesAccount, getFuturesKeysStatus } from '../../services/FuturesService';
import { getUserExchanges } from '../../services/UserExchangesService';
import { getProviders } from '../../services/ExchangeService';
import { getPerformance } from '../../services/OrdersService';
import { getFullBalance } from '../../services/ExchangeService';
import { doLogout } from '../../services/AuthService';
import { getMarketPolicy } from '../../services/SettingsService';
import PieChart from '../../components/PieChart/PieChart';

function money(v) {
    const n = parseFloat(v) || 0;
    return `${n >= 0 ? '+' : ''}$${n.toFixed(2)}`;
}
// Valor absoluto (patrimônio, não P&L) — sem sinal de +/-.
function dollars(v) {
    return `$${(parseFloat(v) || 0).toFixed(2)}`;
}
function pnlClass(v) {
    const n = parseFloat(v) || 0;
    return n > 0 ? 'text-success' : n < 0 ? 'text-danger' : 'text-muted';
}
// ROI (lucro ÷ custo investido). null = sem base de custo → não exibe.
function pctStr(v) {
    if (v === null || v === undefined) return '';
    const n = parseFloat(v);
    return `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`;
}

const SpotIcon = () => (
    <svg width="48" height="48" fill="currentColor" viewBox="0 0 20 20"><path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" /><path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" /></svg>
);
const FuturesIcon = () => (
    <svg width="48" height="48" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd" /></svg>
);

function MarketSelection() {
    const history = useHistory();
    const environment = getCurrentEnvironment();
    const [defaultMarket, setDefaultMarketState] = useState(getDefaultMarket());
    const [spot, setSpot] = useState(null);
    const [futuresAutos, setFuturesAutos] = useState(null); // contagem de automações de FUTUROS
    const [futures, setFutures] = useState(null);
    const [broker, setBroker] = useState(null); // { label, providesFuturesApi }
    const [results, setResults] = useState(null); // { total, byMarket[] } — P&L realizado do período
    const [allocation, setAllocation] = useState(null); // [{ label, value }] alocação spot por ativo
    const [spotEquity, setSpotEquity] = useState(null); // patrimônio spot total (USDT) — snapshot atual
    const [period, setPeriod] = useState(30); // dias do recorte do resultado realizado (30/7/1)
    const [mktPolicy, setMktPolicy] = useState(null); // { futures: { effectiveEnabled, locked, source } }

    useEffect(() => {
        getMarketPolicy()
            .then(setMktPolicy)
            .catch(() => {});

        // Corretora primary atual + se ela oferece API de futuros (providesFuturesApi).
        Promise.all([getUserExchanges(), getProviders().catch(() => ({ exchanges: [] }))])
            .then(([rows, providers]) => {
                const primary = (rows || []).find(r => r.isPrimary && r.isActive);
                if (!primary) return setBroker(null);
                const meta = ((providers && providers.exchanges) || []).find(e => e.name === primary.exchangeName);
                setBroker({
                    label: meta ? meta.label : primary.exchangeName,
                    providesFuturesApi: meta ? meta.providesFuturesApi !== false : true
                });
            })
            .catch(() => setBroker(null));

        // Alocação spot por ativo (valor de mercado das posições) — pizza no banner de portfólio.
        // O mesmo payload dá o patrimônio spot total (soma dos fiatEstimate numéricos, inclui USDT).
        getFullBalance('USDT')
            .then(info => {
                const coins = Object.entries(info || {}).filter(([coin]) => coin !== 'fiatEstimate');
                setAllocation(coins
                    .filter(([coin, d]) => coin !== 'USDT' && parseFloat(d.fiatEstimate) > 0)
                    .map(([coin, d]) => ({ label: coin, value: parseFloat(d.fiatEstimate) })));
                setSpotEquity(coins.reduce((s, [, d]) => s + (parseFloat(d.fiatEstimate) || 0), 0));
            })
            .catch(() => { setAllocation([]); setSpotEquity(null); });

        getAutomationsCounts('spot')
            .then(setSpot)
            .catch(() => setSpot({ error: true }));
        getAutomationsCounts('futures')
            .then(setFuturesAutos)
            .catch(() => setFuturesAutos(null));
        getFuturesKeysStatus()
            .then(status => {
                const configured = environment === 'test' ? status.testConfigured : status.productionConfigured;
                if (!configured) return setFutures({ configured: false });
                return getFuturesAccount(environment)
                    .then(acc => setFutures({ configured: true, ...acc }))
                    .catch(() => setFutures({ configured: true, error: true }));
            })
            .catch(() => setFutures({ error: true }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Resultado REALIZADO do período (Spot × Futuros) — refaz ao trocar o tab de período.
    useEffect(() => {
        setResults(null);
        const start = new Date(Date.now() - period * 24 * 60 * 60 * 1000);
        getPerformance(start, null, environment)
            .then(data => setResults(data && data.results ? data.results : { total: 0, byMarket: [] }))
            .catch(() => setResults({ error: true }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [period]);

    function enter(market) {
        setCurrentMarket(market);
        history.push(getMarketHomePath(market));
    }

    function toggleDefault(market, checked) {
        const next = checked ? market : null;
        setDefaultMarket(next);
        setDefaultMarketState(next);
    }

    function logout() {
        doLogout().finally(() => history.push('/'));
    }

    return (
        <main style={{ minHeight: '100vh', background: '#0b1220', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
            <div className='text-center mb-4'>
                <h2 className='text-white h3'>Escolha o mercado</h2>
                <p className='text-muted'>
                    Ambiente atual: <strong className={environment === 'test' ? 'text-warning' : 'text-success'}>{environment === 'test' ? 'Teste' : 'Produção'}</strong>
                    {broker && <> · Corretora: <strong className='text-info'>{broker.label}</strong></>}
                </p>
            </div>

            {/* Portfólio combinado: P&L realizado do período (Spot + Futuros) + pizzas. */}
            <div className='card card-body shadow border-0 mb-4' style={{ maxWidth: 920, width: '100%', background: '#111c30' }}>
                {/* Patrimônio total (snapshot atual): capital spot + equity de futuros (margem + P&L não realizado). */}
                {(() => {
                    // Equity de futuros = saldo de margem + P&L não realizado das posições abertas.
                    const futEquity = futures && futures.configured && !futures.error && futures.balance
                        ? parseFloat(futures.balance.balance || 0)
                          + (futures.positions || []).reduce((s, p) => s + (parseFloat(p.unRealizedProfit) || 0), 0)
                        : null;
                    // Total mostra assim que o spot carregou; soma futuros quando disponível.
                    const total = spotEquity !== null ? (spotEquity || 0) + (futEquity || 0) : null;
                    const futStr = futures === null ? '…'
                        : (!futures.configured || futures.error) ? '—'
                            : dollars(futEquity);
                    return (
                        <div className='d-flex justify-content-between align-items-end flex-wrap pb-3 mb-3 border-bottom border-secondary'>
                            <div>
                                <span className='text-muted small d-block'>Patrimônio total</span>
                                <h3 className='text-white mb-0'>{total !== null ? dollars(total) : '—'}</h3>
                            </div>
                            <div className='d-flex gap-3 text-end'>
                                <div>
                                    <span className='text-muted small d-block'>🪙 Spot</span>
                                    <strong className='text-white'>{spotEquity !== null ? dollars(spotEquity) : '…'}</strong>
                                </div>
                                <div>
                                    <span className='text-muted small d-block'>⚡ Futuros</span>
                                    <strong className='text-white'>{futStr}</strong>
                                </div>
                            </div>
                        </div>
                    );
                })()}
                {/* Cabeçalho: título + tabs de período + split por mercado */}
                <div className='d-flex justify-content-between align-items-start flex-wrap'>
                    <div>
                        <span className='text-muted small d-block'>Resultado realizado</span>
                        <h3 className={`mb-2 ${pnlClass(results && results.total)}`}>
                            {results && !results.error ? money(results.total) : '—'}
                            {results && !results.error && results.totalPct != null && (
                                <span style={{ fontSize: 15 }}> ({pctStr(results.totalPct)})</span>
                            )}
                        </h3>
                        <div className='btn-group btn-group-sm' role='group'>
                            {[{ d: 30, l: 'Últimos 30 dias' }, { d: 7, l: 'Última semana' }, { d: 1, l: 'Último dia' }].map(opt => (
                                <button key={opt.d} type='button'
                                    className={`btn ${period === opt.d ? 'btn-primary' : 'btn-outline-secondary'}`}
                                    onClick={() => setPeriod(opt.d)}>{opt.l}</button>
                            ))}
                        </div>
                    </div>
                    <div className='d-flex gap-3'>
                        {['Spot', 'Futuros'].map(mkt => {
                            const r = results && !results.error && (results.byMarket || []).find(x => x.key === mkt);
                            return (
                                <div key={mkt} className='text-end'>
                                    <span className='text-muted small d-block'>{mkt === 'Spot' ? '🪙' : '⚡'} {mkt}</span>
                                    <strong className={pnlClass(r ? r.profit : 0)}>
                                        {money(r ? r.profit : 0)}{r && r.pct != null ? <span style={{ fontSize: 11 }}> ({pctStr(r.pct)})</span> : ''}
                                    </strong>
                                    <span className='text-muted d-block' style={{ fontSize: 11 }}>{r ? `${r.trades} trade${r.trades === 1 ? '' : 's'}` : 'sem trades'}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Duas pizzas: operações por mercado (período) × alocação do capital por ativo */}
                <div className='row mt-3 pt-3 border-top border-secondary'>
                    <div className='col-md-6 mb-3 mb-md-0'>
                        <span className='text-muted small d-block mb-2'>Operações por mercado · {period === 30 ? 'últimos 30 dias' : period === 7 ? 'última semana' : 'último dia'}</span>
                        {(() => {
                            const bm = (results && !results.error && results.byMarket) || [];
                            const pie = ['Spot', 'Futuros'].map(mkt => {
                                const r = bm.find(x => x.key === mkt);
                                return { label: `${mkt === 'Spot' ? '🪙' : '⚡'} ${mkt}`, value: r ? r.trades : 0 };
                            });
                            return pie.some(p => p.value > 0)
                                ? <PieChart data={pie} size={140} money={false} color='#e9ecef' minPct={0} />
                                : <span className='text-muted small'>Sem operações fechadas no período.</span>;
                        })()}
                    </div>
                    <div className='col-md-6'>
                        <span className='text-muted small d-block mb-2'>Alocação do capital (spot) por ativo</span>
                        {allocation && allocation.length > 0
                            ? <PieChart data={allocation} size={140} color='#e9ecef' />
                            : <span className='text-muted small'>Sem posições spot.</span>}
                    </div>
                </div>
            </div>

            <div className='row justify-content-center' style={{ maxWidth: 920, width: '100%' }}>
                {/* SPOT */}
                <div className='col-md-6 mb-4'>
                    <div className='card card-body shadow border-0 h-100 d-flex flex-column' style={{ cursor: 'pointer' }} onClick={() => enter(MARKET_SPOT)}>
                        <div className='text-primary mb-2'><SpotIcon /></div>
                        <h4 className='h5'>Spot</h4>
                        <p className='text-muted flex-grow-1' style={{ fontSize: '0.9em' }}>
                            Compra e venda <strong>à vista</strong> de criptomoedas: você possui de fato a moeda.
                            Sem alavancagem, sem liquidação — risco limitado ao valor aplicado.
                        </p>
                        <p className='mb-2' style={{ fontSize: '0.85em' }}>
                            {spot
                                ? (spot.error ? <span className='text-muted'>resumo indisponível</span>
                                    : <>Automações ativas: <strong>{(spot[environment] && spot[environment].active) ?? '—'}</strong> de {(spot[environment] && spot[environment].total) ?? '—'}</>)
                                : <span className='text-muted'>carregando…</span>}
                        </p>
                        <div className='form-check' onClick={e => e.stopPropagation()}>
                            <input className='form-check-input' type='checkbox' id='defSpot'
                                checked={defaultMarket === MARKET_SPOT}
                                onChange={e => toggleDefault(MARKET_SPOT, e.target.checked)} />
                            <label className='form-check-label' htmlFor='defSpot' style={{ fontSize: '0.8em' }}>Entrar direto no Spot após o login</label>
                        </div>
                        <button className='btn btn-primary btn-sm mt-3' onClick={e => { e.stopPropagation(); enter(MARKET_SPOT); }}>Entrar no Spot</button>
                    </div>
                </div>

                {/* FUTUROS */}
                {(() => {
                    const futuresBlocked = mktPolicy && mktPolicy.futures && mktPolicy.futures.effectiveEnabled === false;
                    const futuresBlockedReason = mktPolicy && mktPolicy.futures && mktPolicy.futures.locked
                        ? 'Desabilitado pelo administrador'
                        : 'Desabilitado nas suas preferências';
                    if (futuresBlocked) {
                        return (
                            <div className='col-md-6 mb-4'>
                                <div className='card card-body shadow border-0 h-100 d-flex flex-column' style={{ opacity: 0.5, cursor: 'not-allowed' }}>
                                    <div className='text-muted mb-2'><FuturesIcon /></div>
                                    <h4 className='h5 text-muted'>Futuros <span className='badge bg-secondary'>indisponível</span></h4>
                                    <p className='text-muted flex-grow-1' style={{ fontSize: '0.9em' }}>
                                        Contratos <strong>alavancados</strong> (USDT-M): aposta na alta (long) ou na baixa (short)
                                        sem possuir a moeda. Multiplica ganhos e perdas — tem <strong>risco de liquidação</strong>.
                                    </p>
                                    <div className='alert alert-secondary py-2 mt-auto mb-0' style={{ fontSize: '0.85em' }}>
                                        {futuresBlockedReason}
                                        {mktPolicy.futures.locked && (
                                            <> — contate o administrador para habilitar.</>
                                        )}
                                        {!mktPolicy.futures.locked && (
                                            <> — altere em <strong>Configurações &gt; Política</strong>.</>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    }
                    return (
                        <div className='col-md-6 mb-4'>
                            <div className='card card-body shadow border-0 h-100 d-flex flex-column' style={{ cursor: 'pointer' }} onClick={() => enter(MARKET_FUTURES)}>
                                <div className='text-warning mb-2'><FuturesIcon /></div>
                                <h4 className='h5'>Futuros <span className='badge bg-warning text-dark'>alavancado</span></h4>
                                <p className='text-muted flex-grow-1' style={{ fontSize: '0.9em' }}>
                                    Contratos <strong>alavancados</strong> (USDT-M): aposta na alta (long) ou na baixa (short)
                                    sem possuir a moeda. Multiplica ganhos e perdas — tem <strong>risco de liquidação</strong>.
                                </p>
                                <p className='mb-1' style={{ fontSize: '0.85em' }}>
                                    {spot && spot.error ? <span className='text-muted'>resumo indisponível</span>
                                        : <>Automações ativas: <strong>{(futuresAutos && futuresAutos[environment] && futuresAutos[environment].active) ?? '—'}</strong> de {(futuresAutos && futuresAutos[environment] && futuresAutos[environment].total) ?? '—'}</>}
                                </p>
                                <p className='mb-2' style={{ fontSize: '0.85em' }}>
                                    {broker && !broker.providesFuturesApi
                                        ? <span className='text-warning'>⚠ A corretora <strong>{broker.label}</strong> não oferece futuros no Hydra.</span>
                                        : futures
                                            ? (futures.error ? <span className='text-muted'>resumo indisponível</span>
                                                : !futures.configured ? <span className='text-warning'>chaves não configuradas</span>
                                                    : <>Margem: <strong>{futures.balance ? `${parseFloat(futures.balance.balance).toFixed(2)} USDT` : '—'}</strong> · Posições: <strong>{futures.positions ? futures.positions.length : 0}</strong></>)
                                            : <span className='text-muted'>carregando…</span>}
                                </p>
                                <div className='form-check' onClick={e => e.stopPropagation()}>
                                    <input className='form-check-input' type='checkbox' id='defFut'
                                        checked={defaultMarket === MARKET_FUTURES}
                                        onChange={e => toggleDefault(MARKET_FUTURES, e.target.checked)} />
                                    <label className='form-check-label' htmlFor='defFut' style={{ fontSize: '0.8em' }}>Entrar direto nos Futuros após o login</label>
                                </div>
                                <button className='btn btn-warning btn-sm mt-3' onClick={e => { e.stopPropagation(); enter(MARKET_FUTURES); }}>Entrar nos Futuros</button>
                            </div>
                        </div>
                    );
                })()}
            </div>

            <button className='btn btn-link text-muted mt-2' onClick={logout}>Sair</button>
        </main>
    );
}

export default MarketSelection;
